import { JsonApiRequestError, Mutation } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import { or } from 'patronum';
import { AddWalletMutationParams, AddWalletResponse, createAddWalletMutation } from 'shared/api';
import { createModalFormModel, ModalFormModel, notEmptyRule, notify } from 'shared/lib';

export type AddWalletModelParams = {
	$accountId: Store<number | null>;
	$availableCurrencies: Store<string[]>;
	$isCurrentAccount: Store<boolean>;
};

export type AddWalletFormValue = {
	currency: string | null;
};

export type AddWalletModel = ModalFormModel<null, AddWalletFormValue> & {
	addWalletMutation: Mutation<AddWalletMutationParams, AddWalletResponse, JsonApiRequestError>;
	$pending: Store<boolean>;
	$disabled: Store<boolean>;
	$availableCurrencies: Store<string[]>;
};

export const createAddWalletModel = ({
	$accountId,
	$availableCurrencies,
	$isCurrentAccount,
}: AddWalletModelParams): AddWalletModel => {
	const addWalletMutation = createAddWalletMutation();

	const model = createModalFormModel<null, AddWalletFormValue>({
		fields: {
			currency: {
				init: null,
				rules: [notEmptyRule],
			},
		},
		resetErrorsOnChange: true,
		validateOn: ['submit'],
	});

	const { $pending } = addWalletMutation;

	const $disabled = or(
		$availableCurrencies.map((currencies) => currencies.length === 0),
		$isCurrentAccount,
	);

	sample({
		clock: model.form.submitted,
		source: { values: model.form.$values, accountId: $accountId },
		filter: ({ values }) => values.currency !== null,
		fn: ({ values, accountId }) => ({
			currency: values.currency ?? '	',
			accountId: accountId!,
		}),
		target: addWalletMutation.start,
	});

	chain(addWalletMutation.finished.success, model.switcher.disable);

	notify(addWalletMutation.finished.success, 'add-wallet.success');

	chain(model.modalClosed, addWalletMutation.reset);

	return {
		addWalletMutation,
		$pending,
		$disabled,
		$availableCurrencies,
		...model,
	};
};
